import React, { useEffect, useState } from 'react';
import StepShell from '../StepShell';
import { apiHandler } from '../../../../../api/apihandler';
import { REG_OPTIONS_DETAILS, REG_OPTIONS_STORE } from '../../../../../api/constants';
import { parseOption, apiErrorMessage, apiSucceeded } from '../apiUtils';

const FoodStoryStep = ({ pendingToken, onSubmit, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [groups, setGroups] = useState({ cuisines: [], cookStyles: [], intents: [] });
  const [selectedCuisines, setSelectedCuisines] = useState([]);
  const [selectedCookStyles, setSelectedCookStyles] = useState([]);
  const [selectedIntents, setSelectedIntents] = useState([]);

  const authHeaders = pendingToken ? { 'x-auth-token': pendingToken } : {};

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      const res = await apiHandler(REG_OPTIONS_DETAILS, 'GET', {}, authHeaders);
      if (!isMounted) return;

      if (apiSucceeded(res)) {
        const d = res.data.data;
        setGroups({
          cuisines: (d.cuisines || []).filter((i) => i.status !== 0).map(parseOption),
          cookStyles: (d.howDoYouCooks || []).filter((i) => i.status !== 0).map(parseOption),
          intents: (d.iAmHereTo || []).filter((i) => i.status !== 0).map(parseOption),
        });
      } else {
        setError(apiErrorMessage(res, 'Unable to load food options. Please go back and try again.'));
      }
      setLoading(false);
    };

    load();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (setter) => (label) => {
    setter((prev) => (prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]));
  };

  const toggleCuisine = toggle(setSelectedCuisines);
  const toggleCookStyle = toggle(setSelectedCookStyles);
  const toggleIntent = toggle(setSelectedIntents);

  const selectedCount = selectedCuisines.length + selectedCookStyles.length + selectedIntents.length;
  const canContinue = selectedCuisines.length > 0 && !loading && !saving;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canContinue) return;

    const groupDefs = [
      { option_type: 1, selected: selectedCuisines, options: groups.cuisines },
      { option_type: 2, selected: selectedCookStyles, options: groups.cookStyles },
      { option_type: 3, selected: selectedIntents, options: groups.intents },
    ];

    const payload = groupDefs
      .map((g) => {
        const selectedSet = new Set(g.selected);
        const ids = g.options.filter((o) => selectedSet.has(o.label)).map((o) => o.id);
        return ids.length ? { option_type: g.option_type, option_ids: ids } : null;
      })
      .filter(Boolean);

    setSaving(true);
    setError('');
    const res = await apiHandler(REG_OPTIONS_STORE, 'POST', payload, authHeaders);
    setSaving(false);

    if (apiSucceeded(res)) {
      onSubmit();
    } else {
      setError(apiErrorMessage(res, 'Unable to save your preferences.'));
    }
  };

  const renderChip = (item, selected, onClick) => (
    <div
      key={item.id}
      className={`ob-chip${selected ? ' selected' : ''}`}
      onClick={onClick}
      role="button"
      tabIndex={0}
    >
      <span>{item.emoji} {item.label}</span>
      {selected && <span className="ob-chip-check">✓</span>}
    </div>
  );

  return (
    <StepShell
      step={3}
      title="What's your food story?"
      subtitle="Pick everything that excites you — we'll build your personal feed around it"
      onBack={onBack}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <p className="ob-subtitle">Loading options…</p>
        ) : (
          <>
            <div className="ob-section-label">Cuisines you love</div>
            <div className="ob-chip-grid">
              {groups.cuisines.map((item) =>
                renderChip(item, selectedCuisines.includes(item.label), () => toggleCuisine(item.label)))}
            </div>

            <div className="ob-section-label">How do you cook?</div>
            <div className="ob-chip-grid">
              {groups.cookStyles.map((item) =>
                renderChip(item, selectedCookStyles.includes(item.label), () => toggleCookStyle(item.label)))}
            </div>

            <div className="ob-section-label">I'm here to...</div>
            <div className="ob-chip-grid">
              {groups.intents.map((item) =>
                renderChip(item, selectedIntents.includes(item.label), () => toggleIntent(item.label)))}
            </div>
          </>
        )}

        {error && <p className="ob-error-text">{error}</p>}

        <div className="ob-spacer" />

        <button type="submit" className="ob-btn-primary" disabled={!canContinue}>
          {saving ? 'Saving…' : `Continue with ${selectedCount} selected →`}
        </button>
      </form>
    </StepShell>
  );
};

export default FoodStoryStep;
